// ============================================================================
// LA DATATION D'UNE SAISON — ce que le CODE d'une saison dit de ses dates, quand il le dit.
//
// UTILITÉ : l'âge du stock se lit sur la saison d'origine de l'article, et une saison n'a pas de dates en base. Ce
// module lit le MOTIF du code — une demi-saison (« AH26 », « HW2026 »), un mois de collection (« 092026 »), ou une
// saison permanente (« NOOS ») — et rend les dates qu'il porte. Un code sans motif connu n'a PAS de dates : il est
// « non daté », et l'écran le dit au lieu de le ranger quelque part.
//
// ⚠️ UNE SEULE RÈGLE, ICI : le serveur du back-office (l'âge du stock, l'écoulement, les saisons offertes au choix d'une
//    période — que la barre reçoit toutes datées) et l'écran des Référentiels (les dates qu'une saison tient de son code)
//    la lisent tous deux. Deux tables « AH = septembre → février » divergeraient au premier motif ajouté.
// ⚠️ CE QUE LE CODE DIT EST UNE PROPOSITION : le commerçant pourra corriger les dates d'une saison ; la règle ne sert
//    que là où il n'a rien dit.
// ⚠️ LES MOTIFS SONT CEUX QU'ON A RELEVÉS, pas ceux qu'on imagine : AH/PE (la maquette, le français), HW/FS (relevés
//    sur la bêta — Herbst/Winter, Frühjahr/Sommer, des fournisseurs allemands), AW/FW/SS (leurs équivalents anglais,
//    écrits par les mêmes catalogues), le mois de collection MMAAAA (relevé sur la bêta) et NOOS/PERMANENT.
// ============================================================================

/** Les dates d'une saison, jours `AAAAMMJJ`, bornes incluses. */
export interface SeasonDates {
  from: string;
  to: string;
}

/** Ce qu'un code dit de sa saison : des dates, une saison permanente, ou rien (`null`). */
export type SeasonDating = ({ kind: 'dated' } & SeasonDates) | { kind: 'permanent' };

/** Automne-hiver : 1ᵉʳ septembre de l'année → fin février de la suivante. */
const AUTUMN = new Set(['AH', 'HW', 'AW', 'FW']);
/** Printemps-été : 1ᵉʳ mars → 31 août de l'année. */
const SPRING = new Set(['PE', 'FS', 'SS']);
const HALF_YEAR = /^([A-Z]{2})(\d{2}|\d{4})$/;
const MONTH = /^(0[1-9]|1[0-2])(\d{4})$/;
const PERMANENT = new Set(['PERMANENT', 'NOOS']);

const p2 = (n: number): string => String(n).padStart(2, '0');
/** Le dernier jour de février d'une année (règle grégorienne). */
const februaryEnd = (y: number): number => (y % 4 === 0 && (y % 100 !== 0 || y % 400 === 0) ? 29 : 28);

/** Les dates de la demi-saison d'une année : automne-hiver ou printemps-été. */
function halfYear(autumn: boolean, y: number): SeasonDates {
  return autumn
    ? { from: `${y}0901`, to: `${y + 1}02${p2(februaryEnd(y + 1))}` }
    : { from: `${y}0301`, to: `${y}0831` };
}

/**
 * Les dates que le code d'une saison porte, ou `null` quand il n'en porte aucune.
 * ⚠️ UN MOIS DE COLLECTION SE RANGE DANS LA DEMI-SAISON QUI LE CONTIENT : une livraison de septembre est de l'automne-
 *    hiver — datée de son seul mois, elle deviendrait « saison précédente » dès octobre, alors qu'elle est en rayon.
 *    Janvier et février appartiennent à l'automne-hiver de l'année d'avant.
 */
export function seasonDating(code: string): SeasonDating | null {
  const c = code.trim().toUpperCase();
  if (PERMANENT.has(c)) return { kind: 'permanent' };
  const half = HALF_YEAR.exec(c);
  if (half) {
    const prefix = half[1]!;
    if (!AUTUMN.has(prefix) && !SPRING.has(prefix)) return null;
    const y = half[2]!.length === 2 ? 2000 + Number(half[2]) : Number(half[2]);
    return { kind: 'dated', ...halfYear(AUTUMN.has(prefix), y) };
  }
  const month = MONTH.exec(c);
  if (month) {
    const m = Number(month[1]);
    const y = Number(month[2]);
    if (m >= 3 && m <= 8) return { kind: 'dated', ...halfYear(false, y) };
    return { kind: 'dated', ...halfYear(true, m >= 9 ? y : y - 1) };
  }
  return null;
}

/** Un jour `AAAAMMJJ` qui existe au calendrier — « 20260230 » n'en est pas un. */
export function isCalendarDay(ymd: string): boolean {
  if (!/^\d{8}$/.test(ymd)) return false;
  const y = Number(ymd.slice(0, 4));
  const m = Number(ymd.slice(4, 6));
  const d = Number(ymd.slice(6));
  const t = new Date(Date.UTC(y, m - 1, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === m - 1 && t.getUTCDate() === d;
}

/** Pourquoi des dates de saison posées à la main sont refusées. */
export type SeasonDatesRefusal = 'incomplete' | 'bad_day' | 'reversed';

/**
 * Les dates qu'un commerçant pose sur une saison sont-elles recevables ? Les deux bornes ou aucune (aucune = la saison
 * reprend les dates de son code), des jours réels, le début au plus tard le jour de la fin.
 * ⚠️ UN CODE, PAS UNE PHRASE : le producteur et l'écran refusent par la même règle, chacun dans sa langue.
 */
export function seasonDatesRefusal(from: string | null, to: string | null): SeasonDatesRefusal | null {
  if (from === null && to === null) return null;
  if (from === null || to === null) return 'incomplete';
  if (!isCalendarDay(from) || !isCalendarDay(to)) return 'bad_day';
  return from > to ? 'reversed' : null;
}
